import {
    ModalOverlay,
    ModalDialog,
    ModalBody,
    Button,
    ModalFooter,
    ModalHeader
} from '@flos/react-ui';
import _ from 'lodash';
import { useHippoText } from '../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { CoveragePriceOPC, type CoveragePriceOPCLabels } from './CoveragePriceOPC';
import { periodicityUnit, paymentFrequencyMap } from '../../../utils/currencyUtil';
import { formatArrayToString } from '../../../utils/stringUtil';
import useRemoveCoverageOPC from '../../../recoil/hooks/useRemoveCoverageOPC';
import useOpcJob from '../../../hooks/useOpcJob';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { useOpcInfomation } from '../../../hooks/useOpcInfomation';
import type { CoveragePriceDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type { HandleCloseOPCModal } from '../types';
import HighlightInfoForCoverage from './HighlightInfoForCoverage';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import type { CoverableOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { trackRemoveProduct } from '../../../utils/trackingUtil';

const RemoveCoverageModal = ({
    isOpen,
    handleCloseModal,
    shouldNavigateToShoppingBasket
}: {
    isOpen: boolean;
    handleCloseModal: HandleCloseOPCModal;
    shouldNavigateToShoppingBasket: boolean;
}): React.JSX.Element => {
    const isFullScreen = false;
    const getHippoText = useHippoText();
    const { selectedPaymentFrequency, renewalDate } = useRecoilValue(jobAtom);
    const { removeCoveragePriceDTO, revertChange } = useRemoveCoverageOPC();
    const { addRemoveCoverage } = useOpcInfomation();
    const { createNewPolicyChange, withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { goToShoppingBasket } = usePageNavigation();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const isOpcDataFilled = useRecoilValue(isOpcDataFilledAtom);
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);

    const currencyPostfix =
        periodicityUnit[selectedPaymentFrequency ?? paymentFrequencyMap.monthly];
    const isMonthly = selectedPaymentFrequency === paymentFrequencyMap.monthly;
    const getPrice = (type: 'oldPrice' | 'newPrice'): number => {
        const totalAmount = removeCoveragePriceDTO?.[type];
        return isMonthly
            ? totalAmount?.monthlyTotal?.amount ?? 0
            : totalAmount?.yearlyTotal?.amount ?? 0;
    };
    const oldTotalPrice = getPrice('oldPrice');
    const newTotalPrice = getPrice('newPrice');
    const selectedRemoveCoverage = removeCoveragePriceDTO?.removeCoveragePrice;
    const dependentCoverageList = removeCoveragePriceDTO?.dependentCoveragesPrice ?? [];
    const dependentCoverageName = formatArrayToString(
        dependentCoverageList.map((coverage) => coverage.coverageName),
        ',',
        'og'
    );
    const totalCoverageToRemove = [...dependentCoverageList, selectedRemoveCoverage];
    const totalRemoveCoveragePrice = totalCoverageToRemove.reduce(
        (sum: number, coverage: CoveragePriceDTO | undefined) => {
            return isMonthly
                ? sum + ((coverage?.coveragePrice?.monthlyTotal?.amount as number) ?? 0)
                : sum + ((coverage?.coveragePrice?.yearlyTotal?.amount as number) ?? 0);
        },
        0
    );

    const labels = {
        effectiveDateSubTitle: getHippoText('DSS-ADD-SIMPLE-COVERAGE-START-DATE-QUESTION'),
        datefieldPlaceholder: getHippoText('DSS-ADD-SIMPLE-COVERAGE-START-DATE-PLACEHOLDER'),
        cancelBtnText: getHippoText('DSS-COVERABLE-DETAILS-CANCEL'),
        submitBtnText: getHippoText('DSS-COVERABLE-DETAILS-REMOVE-COVERAGE')
    };
    const coveragePriceOPCLabels: CoveragePriceOPCLabels = {
        title: `${getHippoText('DSS-COVERABLE-DETAILS-REMOVE-COVERAGE')}: ${
            (removeCoveragePriceDTO?.coverageName as string) ?? ''
        }`,
        ...(dependentCoverageList &&
            !_.isEmpty(dependentCoverageList) && {
                subtitle: `${getHippoText('DSS-COVERABLE-DETAILS-REMOVE-COVERAGE-PAYMENT', {
                    dependent_coverage_name: dependentCoverageName
                })}`
            }),
        priceChangeSubTitle: getHippoText('DSS-COVERABLE-DETAILS-REMOVE-COVERAGE-DETAILS', {
            flavor: removeCoveragePriceDTO?.coverableName ?? ''
        }),
        coverablePriceBefore: getHippoText('DSS-COVERABLE-DETAILS-PRICE-AFFECT-BEFORE'),
        coverablePriceAfter: getHippoText('DSS-COVERABLE-DETAILS-PRICE-AFFECT-AFTER')
    };
    const trackRemovedCoverage: CoverableOnsaDTO = {
        flavourName: removeCoveragePriceDTO?.coverableName ?? '',
        monthlyPrice: removeCoveragePriceDTO?.newPrice?.monthlyTotal,
        yearlyPrice: removeCoveragePriceDTO?.newPrice?.yearlyTotal,
        coverages: [
            {
                coverageId: addRemoveCoverage.coverageId,
                coverageName: removeCoveragePriceDTO?.coverageName,
                coveragePrice: {
                    monthlyPrice: {
                        ...totalCoverageToRemove[0]?.coveragePrice.monthlyTotal,
                        amount: totalCoverageToRemove[0]?.coveragePrice.monthlyTotal.amount
                            ? -totalCoverageToRemove[0].coveragePrice.monthlyTotal?.amount
                            : undefined
                    },
                    yearlyPrice: {
                        ...totalCoverageToRemove[0]?.coveragePrice.yearlyTotal,
                        amount: totalCoverageToRemove[0]?.coveragePrice.yearlyTotal.amount
                            ? -totalCoverageToRemove[0].coveragePrice.yearlyTotal.amount
                            : undefined
                    }
                }
            }
        ],
        publicId: '',
        termIds: [],
        fixedId: '',
        lobCode: ''
    };

    const handleClearChange = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        await createNewPolicyChange();
    };

    const handleRevertChange = async (): Promise<void> => {
        await revertChange();
        shouldNavigateToShoppingBasket && goToShoppingBasket(false);
    };

    const closeCallback = isFromDAFlow ? handleRevertChange : handleClearChange;

    if (!removeCoveragePriceDTO) return <></>;
    return (
        <ModalOverlay isOpen={isOpen} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(closeCallback);
                }}
            >
                <ModalHeader />
                <ModalBody>
                    <CoveragePriceOPC
                        labels={coveragePriceOPCLabels}
                        coveragePrice={totalRemoveCoveragePrice}
                        oldTotalPrice={oldTotalPrice}
                        newTotalPrice={newTotalPrice}
                        currencyPostfix={currencyPostfix}
                        currencyPriceChangePrefix="-"
                    ></CoveragePriceOPC>
                    <HighlightInfoForCoverage
                        coverageId={addRemoveCoverage.coverageId}
                        editCoverageType="remove"
                        coverageName={removeCoveragePriceDTO.coverageName}
                        renewalDate={renewalDate}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={isOpcFetching || !isOpcDataFilled}
                        onClick={() => {
                            trackRemoveProduct(trackRemovedCoverage);
                            handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                        }}
                    >
                        {labels.submitBtnText}
                    </Button>
                    <Button
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(closeCallback);
                        }}
                        disabled={isOpcFetching}
                    >
                        {labels.cancelBtnText}
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default RemoveCoverageModal;
